import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Select, Spin, Button, Divider, Space, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useCompany } from '../../contexts/CompanyContext';
import { useConfig } from '../../contexts/ConfigContext';
import { UnifiedContactModal } from '@ryo/ui';
import { useSecureRequest } from '../../hooks/useSecureRequest';

interface ContactSearchSelectProps {
  value?: number;
  onChange?: (value: number) => void;
  onSelectContact?: (contact: any) => void;
  role?: string;
  placeholder?: string;
  open?: boolean;
}
interface RenderElementProps {
  menu: React.ReactNode;
  setShowContactModal: (value: boolean) => void;
  role: string;
}
const RenderElement = ({menu,setShowContactModal,role}: RenderElementProps) => {
  return (
    <>
      {menu}
      <Divider style={{ margin: '8px 0' }} />
      <Space style={{ padding: '0 8px 4px', display: 'flex', justifyContent: 'center' }}>
        <Button
          type="dashed"
          icon={<PlusOutlined />}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setShowContactModal(true)}
          size="small"
          style={{ width: '100%' }}
        >
          Crear {role === 'PROVIDER' ? 'Proveedor' : 'Contacto'}
        </Button>
      </Space>
    </>
  );
}

export const ContactSearchSelect: React.FC<ContactSearchSelectProps> = ({ 
  value, 
  onChange, 
  onSelectContact,
  role = 'PROVIDER', 
  placeholder = 'Buscar persona...',
  open,
}) => {
  const { company } = useCompany();
  const { config } = useConfig();
  const { securePost } = useSecureRequest();
  
  const [fetching, setFetching] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const [showContactModal, setShowContactModal] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const fetchOptions = useCallback((query: string = '') => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(async () => {
      if (!company?.current?.uid) return;
      setFetching(true);
      try {
        const response = await securePost('/user/contact/search-bulk-contacts', {
          company: company.current.uid,
          search: query.trim(),
          type: 'light',
          role: role
        });
        if (response?.contactList) {
          setData(response.contactList);
        }
      } catch (error) {
        console.error('Error searching contacts:', error);
      } finally {
        setFetching(false);
      }
    }, 500);
  }, [company?.current?.uid, role, securePost]);

  useEffect(() => {
    if (company?.current?.uid) {
      fetchOptions('');
    }
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    }
  }, [company?.current?.uid, fetchOptions]);

  const handleSaveContact = async (values: any) => {
    const dataRequest: any = {
      company: company.current?.uid,
      module: config.module,
      contact: { ...values, company_id: company.current?.id, roles: [{ role_type: role }] }
    };
    const res = await securePost("/user/contact/create-contact", dataRequest);
    if (res.status !== 200) throw new Error(res.message || "Error al guardar el contacto");
    return res.contact;
  };

  const handleCreateSuccess = (newContact: any) => {
    setShowContactModal(false);
    message.success('Contacto creado exitosamente');
    setData(prev => {
      const exists = prev.find(c => c.id === newContact.id);
      if (!exists) return [...prev, newContact];
      return prev;
    });
    // Let React update the list first, then trigger selection so value exists in options
    setTimeout(() => {
      if (onChange && newContact.id) {
        onChange(newContact.id);
      }
      if (onSelectContact && newContact) {
        onSelectContact(newContact);
      }
    }, 100);
  };

  const onDropdownVisibleChange = (open: boolean) => {
    // Refresh list slightly when opened if empty
    if (open && data.length === 0) {
      fetchOptions('');
    }
  };

  const renderPopup = (menu: React.ReactElement) => {
    return <RenderElement menu={menu} setShowContactModal={setShowContactModal} role={role} />;
  };

  return (
    <>
      <Select
        showSearch
        open={open}
        value={value}
        allowClear
        placeholder={placeholder}
        notFoundContent={fetching ? <Spin size="small" /> : 'Sin resultados'}
        filterOption={false}
        onSearch={fetchOptions}
        onChange={(val, opt) => {
          if (onChange) onChange(val);
          if (onSelectContact) {
            const found = data.find(item => item.id === val);
            onSelectContact(found);
          }
        }}
        onOpenChange={onDropdownVisibleChange}
        style={{ width: '100%' }}
        options={data.map(item => ({ label: item.name || item.fullName || 'Sin nombre', value: item.id }))}
        popupRender={renderPopup}
      />
      <UnifiedContactModal
        visible={showContactModal}
        onCancel={() => setShowContactModal(false)}
        onSuccess={handleCreateSuccess}
        onSave={handleSaveContact}
        role={role as any}
        validationMode="strict"
      />
    </>
  );
};
